import React, { useState } from "react";
import {
  FaBolt,
  FaBuildingColumns,
  FaChartBar,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaFilter,
  FaGlobe,
  FaMagnifyingGlass,
  FaSpinner,
} from "react-icons/fa6";
import { INDUSTRIES, SOURCES } from "../appShared";

export default function FindTab({
  sources,
  setSources,
  industry,
  setIndustry,
  city,
  setCity,
  keyword,
  setKeyword,
  cities,
  oppCountries,
  setOppCountries,
  oppLimit,
  setOppLimit,
  oppRefineCountries,
  setOppRefineCountries,
  opportunityCountryOptions,
  selectedSourceLabels,
  searching,
  searchStatus,
  dbSaveStatus,
  searchError,
  handleSearch,
  topCountryCounts,
}) {
  const [criteriaOpen, setCriteriaOpen] = useState(true);

  const quickSearches = [
    "No website businesses",
    "Restaurants without online presence",
    "Small construction companies",
    "IT consulting firms",
    "Marketing agencies",
    "Retail shops",
    "Beauty salons",
    "Fitness gyms",
  ];

  const toggleSource = (id) => {
    setSources((previous) =>
      previous.includes(id)
        ? previous.filter((source) => source !== id)
        : [...previous, id]
    );
  };

  const getSourceFlagCode = (source) => {
    if (source.flag) return source.flag;
    if (source.id.length === 2) return source.id.toLowerCase();
    return "";
  };

  const renderSourceIcon = (source, size = "32x24") => {
    if (source.id === "OPP") {
      return (
        <span className="ws-source-symbol">
          <FaChartBar />
        </span>
      );
    }

    if (source.id === "DOFFIN" || source.type === "TENDER") {
      return (
        <span className="ws-source-symbol">
          <FaBuildingColumns />
        </span>
      );
    }

    const flagCode = getSourceFlagCode(source);

    if (!flagCode) {
      return (
        <span className="ws-source-symbol">
          <FaGlobe />
        </span>
      );
    }

    return (
      <img
        className="ws-source-flag"
        src={`https://flagcdn.com/${size}/${flagCode}.png`}
        alt=""
        onError={(event) => {
          event.currentTarget.style.display = "none";
        }}
      />
    );
  };

  const toggleOppCountry = (id) => {
    setOppCountries((previous) => {
      if (id === "GLOBAL") return ["GLOBAL"];

      const withoutGlobal = previous.filter((value) => value !== "GLOBAL");

      if (withoutGlobal.includes(id)) {
        const next = withoutGlobal.filter((value) => value !== id);
        return next.length ? next : ["GLOBAL"];
      }

      return [...withoutGlobal, id];
    });
  };

  const selectAllOppCountries = () => {
    setOppCountries(
      opportunityCountryOptions
        .filter((country) => country.id !== "GLOBAL")
        .map((country) => country.id)
    );
  };

  const selectedOppLabels = oppCountries.includes("GLOBAL")
    ? ["Global"]
    : opportunityCountryOptions
        .filter((country) => oppCountries.includes(country.id))
        .map((country) => country.label);

  const isOppGlobalMode =
    !oppRefineCountries ||
    oppCountries.includes("GLOBAL") ||
    selectedOppLabels.length === 0;

  const oppSummaryLabel = isOppGlobalMode
    ? "Global feed"
    : `${selectedOppLabels.length} countries`;

  const searchButtonLabel =
    sources.length === 1 && sources.includes("OPP")
      ? "Search Public Opportunities"
      : sources.length > 0
        ? `Find Leads from ${sources.length} Source${sources.length === 1 ? "" : "s"}`
        : "Choose Sources";


  const insightSources = SOURCES.filter(
    (source) =>
      source.id !== "AI" &&
      source.id !== "OPP" &&
      source.id !== "DOFFIN"
  ).slice(0, 6);

  const insightCounts = insightSources.map((source) =>
    Number(topCountryCounts?.[source.label] || 0)
  );

  const maxCountryCount = Math.max(...insightCounts, 1);
  const totalInsightCount = insightCounts.reduce(
    (sum, count) => sum + count,
    0
  );

  return (
    <div className={`ws-find-page ${searching ? "is-searching" : ""}`}>
      <section className="ws-search-card">
        <div className="ws-search-heading">
          <div>
            <span className="ws-eyebrow">
              <FaMagnifyingGlass />
              Lead discovery
            </span>

            <h1>Find qualified business opportunities</h1>

            <p>
              Search companies, public registries and tender sources across
              multiple markets.
            </p>
          </div>

          <div className="ws-selected-total">
            <strong>{sources.length}</strong>
            <span>sources selected</span>
          </div>
        </div>

        <div className="ws-source-summary">
          <div className="ws-source-summary-copy">
            <span className="ws-source-summary-label">Lead sources</span>

            <strong>
              {sources.length === 0
                ? "No sources selected"
                : `${sources.length} source${sources.length === 1 ? "" : "s"} ready`}
            </strong>

            <small>
              Select the company markets and opportunity databases you want to
              search.
            </small>
          </div>

          <div className="ws-source-actions">
            <button
              type="button"
              onClick={() => setSources(SOURCES.map((source) => source.id))}
            >
              Select all
            </button>

            <span />

            <button type="button" onClick={() => setSources([])}>
              Clear
            </button>
          </div>
        </div>

        <div className="ws-source-grid">
          {SOURCES.map((source) => {
            const selected = sources.includes(source.id);

            return (
              <button
                key={source.id}
                type="button"
                className={`ws-source-option ${
                  selected ? "selected" : ""
                }`}
                onClick={() => toggleSource(source.id)}
                aria-pressed={selected}
              >
                <span className="ws-source-option-icon">
                  {renderSourceIcon(source)}
                </span>

                <span className="ws-source-option-copy">
                  <strong>{source.label}</strong>

                  <small>
                    {source.type === "TENDER"
                      ? "Public tender source"
                      : source.type === "PUBLIC"
                        ? "Public opportunities"
                        : `${source.id} company market`}
                  </small>
                </span>

                <span className="ws-source-check">
                  {selected && <FaCheck />}
                </span>
              </button>
            );
          })}
        </div>

        {sources.includes("OPP") && (
          <div className="ws-opportunity-settings">
            <div className="ws-opportunity-heading">
              <div>
                <strong>Public opportunity settings</strong>
                <span>{oppSummaryLabel}</span>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (oppRefineCountries) {
                    setOppRefineCountries(false);
                    setOppCountries(["GLOBAL"]);
                  } else {
                    setOppRefineCountries(true);

                    if (oppCountries.includes("GLOBAL")) {
                      setOppCountries(["US", "GB", "AE"]);
                    }
                  }
                }}
              >
                {oppRefineCountries ? "Use global feed" : "Refine countries"}
              </button>
            </div>

            <div className="ws-opportunity-toolbar">
              <label>
                <span>Maximum results</span>

                <select
                  value={oppLimit}
                  onChange={(event) => setOppLimit(Number(event.target.value))}
                >
                  {[20, 50, 100, 150, 200].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>

              {oppRefineCountries && (
                <div className="ws-opportunity-actions">
                  <button type="button" onClick={() => setOppCountries(["GLOBAL"])}>
                    Global only
                  </button>

                  <button type="button" onClick={selectAllOppCountries}>
                    Select all countries
                  </button>
                </div>
              )}
            </div>

            {oppRefineCountries && (
              <div className="ws-opportunity-country-grid">
                {opportunityCountryOptions
                  .filter((country) => country.id !== "GLOBAL")
                  .map((country) => (
                    <label
                      key={country.id}
                      className={oppCountries.includes(country.id) ? "selected" : ""}
                    >
                      <input
                        type="checkbox"
                        checked={oppCountries.includes(country.id)}
                        onChange={() => toggleOppCountry(country.id)}
                      />

                      <span>{country.label}</span>
                    </label>
                  ))}
              </div>
            )}
          </div>
        )}

        <div className={`ws-filter-section ${criteriaOpen ? "open" : "collapsed"}`}>
          <button
            type="button"
            className="ws-section-heading ws-section-toggle"
            onClick={() => setCriteriaOpen((previous) => !previous)}
            aria-expanded={criteriaOpen}
          >
            <span className="ws-section-heading-main">
              <FaFilter />

              <span>
                <strong>Search criteria</strong>
                <small>Define the type of businesses and opportunities to find</small>
              </span>
            </span>

            {criteriaOpen ? <FaChevronUp /> : <FaChevronDown />}
          </button>

          {criteriaOpen && (
            <div className="ws-filter-content">
              <div className="ws-filter-grid">
                <label className="ws-field">
                  <span>Industry</span>

                  <select
                    value={industry}
                    onChange={(event) => setIndustry(event.target.value)}
                  >
                    {INDUSTRIES.map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>

                <label className="ws-field">
                  <span>City</span>

                  <select
                    value={city}
                    onChange={(event) => setCity(event.target.value)}
                  >
                    {cities.map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>

                <label className="ws-field ws-keyword-field">
                  <span>
                    Keyword
                    <small>Optional</small>
                  </span>

                  <div className="ws-keyword-input">
                    <FaMagnifyingGlass />

                    <input
                      value={keyword}
                      placeholder='e.g. "no website", "startup"'
                      onChange={(event) => setKeyword(event.target.value)}
                      onKeyDown={(event) => {
                        if (
                          event.key === "Enter" &&
                          sources.length > 0 &&
                          !searching
                        ) {
                          handleSearch();
                        }
                      }}
                    />
                  </div>
                </label>
              </div>

              <div className="ws-quick-searches">
                <span>Quick searches</span>

                <div>
                  {quickSearches.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={keyword === value ? "active" : ""}
                      onClick={() => setKeyword(value)}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {(searchError || searchStatus || dbSaveStatus) && (
          <div
            className={`ws-search-message ${
              searchError ? "error" : "active"
            }`}
          >
            {searching && !searchError && <FaSpinner className="spin" />}

            <span>{searchError || searchStatus || dbSaveStatus}</span>
          </div>
        )}

        <div className="ws-search-action">
          <div>
            <strong>Ready to discover leads?</strong>

            <span>
              Search results will be saved directly to your pipeline.
            </span>
          </div>

          <button
            type="button"
            onClick={handleSearch}
            disabled={searching || sources.length === 0}
          >
            {searching ? (
              <>
                <FaSpinner className="spin" />
                {searchStatus || "Searching"}
              </>
            ) : (
              <>
                <FaBolt />
                {searchButtonLabel}
              </>
            )}
          </button>
        </div>
      </section>

      <section className="ws-insights-card">
        <div className="ws-insights-heading">
          <div>
            <span>Pipeline insights</span>
            <h2>Top countries by leads</h2>
          </div>

          <small>From your pipeline</small>
        </div>

        {totalInsightCount === 0 ? (
          <div className="ws-empty-state">
            <span>
              <FaChartBar />
            </span>

            <div>
              <strong>No lead insights yet</strong>

              <p>
                Run your first search to see market distribution and source
                performance.
              </p>
            </div>
          </div>
        ) : (
          <div className="ws-country-list">
            {insightSources.map((source) => {
              const count = Number(
                topCountryCounts?.[source.label] || 0
              );

              return (
                <div key={source.id} className="ws-country-row">
                  <span className="ws-country-name">
                    {renderSourceIcon(source, "24x18")}
                    {source.label}
                  </span>

                  <div className="ws-country-track">
                    <span
                      style={{
                        width:
                          count > 0
                            ? `${(count / maxCountryCount) * 100}%`
                            : "0%",
                      }}
                    />
                  </div>

                  <strong>{count}</strong>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

